
import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { BarChart3, TrendingUp, Download, Layers, Calendar, ArrowUpCircle, ArrowDownCircle, Target } from 'lucide-react';
import { Transaction } from '../../types';

interface AnalyticsProps {
  metrics: any;
  transactions: Transaction[];
  selectedMonth: string;
}

export const FinancialAnalytics = ({ metrics, transactions, selectedMonth }: AnalyticsProps) => {
  const chartData = metrics.categories
    .map((c: any) => ({ name: c.label, value: c.actual, color: c.color }))
    .filter((d: any) => d.value > 0);

  const handleExport = () => {
    if (transactions.length === 0 && metrics.categories.length === 0) {
      alert("No data available to export.");
      return;
    }

    const txHeaders = ["Date", "Description", "Category", "Type", "Amount"];
    const txRows = transactions.map(tx => [
      tx.date,
      `"${tx.name.replace(/"/g, '""')}"`,
      tx.category,
      tx.type,
      tx.amount
    ]);

    const budgetHeaders = ["Budget Category", "Limit", "Actual Spend", "Remaining"];
    const budgetRows = metrics.categories.map((c: any) => [
      c.label,
      c.limit,
      c.actual,
      c.limit - c.actual
    ]);

    const summaryHeaders = ["Metric", "Value"];
    const summaryRows = [
      ["Report Month", selectedMonth],
      ["Total Income", metrics.income],
      ["Total Expenses", metrics.expenses],
      ["Monthly Surplus", metrics.surplus],
      ["Savings Rate %", metrics.savingsRate],
      ["Stack Score", metrics.score]
    ];

    const csvContent = [
      ["--- MONTHLY SUMMARY ---"],
      summaryHeaders.join(","),
      ...summaryRows.map((row: any[]) => row.join(",")),
      [], 
      ["--- BUDGET PERFORMANCE ---"],
      budgetHeaders.join(","),
      ...budgetRows.map((row: any[]) => row.join(",")),
      [], 
      ["--- TRANSACTION LOG ---"],
      txHeaders.join(","),
      ...txRows.map((row: any[]) => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    
    link.setAttribute("href", url);
    link.setAttribute("download", `StackUp_Summary_${selectedMonth.replace(' ', '_')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-8 animate-fade-in pb-12">
      {/* Month-End Summary Section */}
      <div className="fin-card p-8 rounded-4xl bg-navy text-white border-none shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-electric/10 rounded-full blur-3xl -mr-20 -mt-20"></div>
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight">Month-End Summary</h2>
            <p className="text-xs text-sky font-bold uppercase tracking-[0.2em] mt-1">{selectedMonth}</p>
          </div>
          <button 
            onClick={handleExport}
            className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
          >
            <Download size={14} /> Export Report
          </button>
        </div>

        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-6">
          <SummaryBlock icon={<ArrowUpCircle className="text-emerald-400" />} label="Total Income" value={`R${metrics.income.toLocaleString()}`} />
          <SummaryBlock icon={<ArrowDownCircle className="text-rose-400" />} label="Total Outflow" value={`R${metrics.expenses.toLocaleString()}`} />
          <SummaryBlock icon={<Target className="text-sky" />} label="Net Surplus" value={`R${metrics.surplus.toLocaleString()}`} />
          <SummaryBlock icon={<TrendingUp className="text-emerald" />} label="Efficiency" value={`${metrics.savingsRate}%`} />
        </div>
      </div>

      <div className="fin-card p-8 rounded-4xl">
        <div className="flex items-center gap-4 mb-12">
          <div className="p-3 bg-emerald/10 text-emerald rounded-2xl border border-emerald/20">
            <BarChart3 size={28} />
          </div>
          <div>
            <h2 className="text-2xl font-extrabold text-navy tracking-tight">Performance Audit</h2>
            <p className="text-xs font-inter text-slate-400 uppercase font-bold tracking-widest">Budget Stack Analysis</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Pie Chart Visual */}
          <div className="h-[340px] relative min-w-0">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    innerRadius={80}
                    outerRadius={120}
                    paddingAngle={10}
                    dataKey="value"
                    stroke="none"
                  >
                    {chartData.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={entry.color} className="outline-none" />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{backgroundColor: '#fff', border: '1px solid #F1F5F9', borderRadius: '12px', fontSize: '12px'}} 
                  />
                  <Legend 
                    iconType="circle" 
                    wrapperStyle={{fontSize: '9px', textTransform: 'uppercase', fontWeight: '800', letterSpacing: '0.1em', paddingTop: '20px', color: '#001F3F'}} 
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center border-2 border-dashed border-slate-100 rounded-3xl text-slate-300 text-sm font-bold uppercase tracking-widest">
                No transactions for this period
              </div>
            )}
            
            {/* Center Summary */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-14 text-center pointer-events-none">
              <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none">Monthly Outflow</div>
              <div className="text-xl font-bold text-navy mt-1 tracking-tight">R{metrics.expenses.toLocaleString()}</div>
            </div>
          </div>

          {/* Efficiency KPIs */}
          <div className="space-y-6 min-w-0">
            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={16} className="text-emerald" />
                <span className="text-[10px] font-inter text-slate-400 font-bold uppercase tracking-widest">Retention Velocity</span>
              </div>
              <div className="text-3xl font-extrabold text-navy leading-none tracking-tighter">
                {metrics.savingsRate}%
              </div>
              <div className="h-1.5 w-full bg-white rounded-full mt-4 overflow-hidden border border-slate-100">
                <div className="h-full bg-emerald" style={{ width: `${Math.min(100, Math.max(0, metrics.savingsRate))}%` }}></div>
              </div>
              <p className="text-[10px] font-inter text-slate-400 mt-3 font-bold uppercase tracking-tight">Income retained after expenses for this month.</p>
            </div>

            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
              <div className="flex items-center gap-2 mb-2">
                <Layers size={16} className="text-electric" />
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Month Score</span>
              </div>
              <div className="text-3xl font-extrabold text-navy leading-none tracking-tighter">
                {metrics.score}/100
              </div>
              <div className="h-1.5 w-full bg-white rounded-full mt-4 overflow-hidden border border-slate-100">
                <div className="h-full bg-navy" style={{ width: `${metrics.score}%` }}></div>
              </div>
              <p className="text-[10px] text-slate-400 mt-3 font-bold uppercase tracking-tight">Audit rating based on monthly surplus targets.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryBlock = ({ icon, label, value }: { icon: React.ReactNode, label: string, value: string }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 text-sky/60">
      {icon}
      <span className="text-[9px] font-inter font-black uppercase tracking-widest">{label}</span>
    </div>
    <div className="text-xl font-bold tracking-tight text-white">{value}</div>
  </div>
);
